import { cn } from '@/lib/utils';

export const DEFAULT_PROPERTIES_SKELETON_CARD_COUNT = 20;
export const DEFAULT_PROPERTIES_MOBILE_SHEET_ROW_COUNT = 6;

export type PropertiesCardsViewport = 'desktop' | 'mobile';
export type PropertiesPageSkeletonViewport = 'desktop' | 'mobile-map' | 'mobile-list';

type PropertiesCardsSurfaceSkeletonProps = {
  viewport: PropertiesCardsViewport;
  cardCount?: number;
  bare?: boolean;
  includeHeader?: boolean;
  includePagination?: boolean;
  includePromoCard?: boolean;
  className?: string;
};

type PropertiesMapSurfaceSkeletonProps = {
  viewport: PropertiesCardsViewport;
  className?: string;
  showListPill?: boolean;
  mobileBottomInsetPx?: number;
};

type PropertiesPageSkeletonProps = {
  viewport: PropertiesPageSkeletonViewport;
  cardCount?: number;
};

function PropertiesDesktopHeaderSkeleton() {
  return (
    <header className="sticky top-0 z-50 flex h-[76px] items-center justify-between border-b border-gray-100 bg-white px-4 py-3 dark:border-zinc-900 dark:bg-black sm:px-6">
      <div className="h-10 w-28 rounded-md skeleton-shimmer md:w-[180px]" />

      <div className="hidden h-12 w-[480px] items-center rounded-full border border-gray-100 bg-white p-1 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 lg:flex">
        <div className="mr-1 h-full flex-1 rounded-l-full skeleton-shimmer" />
        <div className="my-auto h-6 w-px bg-gray-200 dark:bg-zinc-800" />
        <div className="mx-1 h-full flex-1 skeleton-shimmer" />
        <div className="my-auto h-6 w-px bg-gray-200 dark:bg-zinc-800" />
        <div className="mx-1 h-full w-24 skeleton-shimmer" />
        <div className="ml-1 h-10 w-10 rounded-full skeleton-shimmer" />
      </div>

      <div className="flex items-center gap-3">
        <div className="hidden h-10 w-[100px] rounded-full skeleton-shimmer md:block" />
        <div className="hidden h-10 w-[180px] rounded-full skeleton-shimmer xl:block" />
        <div className="h-10 w-10 rounded-full skeleton-shimmer" />
        <div className="h-10 w-10 rounded-full skeleton-shimmer" />
        <div className="h-10 w-10 rounded-full skeleton-shimmer" />
      </div>
    </header>
  );
}

function PropertiesMobileHeaderSkeleton() {
  return (
    <header className="sticky top-0 z-50 border-b border-gray-100 bg-white px-4 dark:border-zinc-900 dark:bg-black">
      <div className="flex h-[76px] items-center gap-2.5">
        <div className="h-10 w-10 rounded-full skeleton-shimmer" />

        <div className="flex min-w-0 flex-1 items-center gap-2.5 rounded-full border border-gray-100 bg-white px-3.5 py-2.5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
          <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-900">
            <div className="h-4 w-4 rounded-full skeleton-shimmer" />
          </div>

          <div className="min-w-0 flex-1 space-y-1">
            <div className="h-3.5 w-28 rounded-full skeleton-shimmer" />
            <div className="h-3 w-24 rounded-full skeleton-shimmer" />
          </div>
        </div>

        <div className="h-10 w-10 rounded-full skeleton-shimmer" />
      </div>
    </header>
  );
}

function PropertiesDesktopCardSkeleton() {
  return (
    <div className="flex flex-col gap-4 rounded-[32px] border border-gray-50 bg-white p-3 shadow-sm dark:border-zinc-900 dark:bg-zinc-950 dark:shadow-[0_16px_40px_rgba(0,0,0,0.35)]">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[24px] skeleton-shimmer">
        <div className="absolute left-4 top-4 h-7 w-28 rounded-full bg-white/60 dark:bg-black/35" />
        <div className="absolute right-4 top-4 h-9 w-9 rounded-full bg-white/60 dark:bg-black/35" />
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5">
          <div className="h-1.5 w-4 rounded-full bg-white/60 dark:bg-white/35" />
          <div className="h-1.5 w-1.5 rounded-full bg-white/60 dark:bg-white/30" />
          <div className="h-1.5 w-1.5 rounded-full bg-white/60 dark:bg-white/30" />
        </div>
      </div>

      <div className="flex flex-col gap-3 px-2 pb-2">
        <div className="h-3 w-24 rounded-full skeleton-shimmer" />
        <div className="h-5 w-3/4 rounded-md skeleton-shimmer" />

        <div className="flex flex-col gap-1.5">
          <div className="h-3 w-full rounded-full skeleton-shimmer" />
          <div className="h-3 w-2/3 rounded-full skeleton-shimmer" />
        </div>

        <div className="mt-2 flex items-center justify-between">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="flex flex-col items-center gap-1 opacity-60">
              <div className="h-5 w-6 rounded skeleton-shimmer" />
              <div className="h-2 w-8 rounded-full skeleton-shimmer" />
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <div className="h-7 w-32 rounded-md skeleton-shimmer" />
          <div className="h-3 w-40 rounded-full skeleton-shimmer" />
          <div className="mt-2 h-12 w-full rounded-full skeleton-shimmer" />
        </div>
      </div>
    </div>
  );
}

function PropertiesPromoCardSkeleton() {
  return (
    <div className="flex min-h-[500px] h-full flex-col rounded-[32px] bg-slate-50 p-6 dark:bg-zinc-950 dark:ring-1 dark:ring-zinc-900">
      <div className="mx-auto mb-8 h-8 w-32 rounded-full skeleton-shimmer" />
      <div className="mb-4 h-10 w-3/4 rounded-lg skeleton-shimmer" />
      <div className="mb-8 h-10 w-1/2 rounded-lg skeleton-shimmer" />

      <div className="mt-auto space-y-4">
        <div className="h-4 w-full rounded-full skeleton-shimmer" />
        <div className="h-4 w-5/6 rounded-full skeleton-shimmer" />
        <div className="h-4 w-4/5 rounded-full skeleton-shimmer" />
      </div>

      <div className="mt-12 flex items-end justify-between">
        <div className="h-10 w-32 rounded-lg skeleton-shimmer" />
        <div className="h-12 w-12 rounded-full skeleton-shimmer" />
      </div>
    </div>
  );
}

// Vertical mobile card skeleton — mirrors MobilePropertyCard structure 1:1
// (aspect-[4/3] image with badges, location, title, attributes row, price/microcopy).
// Replaces the legacy horizontal drawer-row skeleton.
export function PropertiesMobileVerticalCardSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn('w-full', className)}>
      {/* Image area — aspect 4/3 with overlays matching real card */}
      <div className="relative mb-3 aspect-[4/3] w-full overflow-hidden rounded-[1.6rem] skeleton-shimmer">
        <div className="absolute left-3 top-3 h-7 w-24 rounded-full bg-white/40 backdrop-blur-sm dark:bg-black/35" />
        <div className="absolute right-3 top-3 h-8 w-8 rounded-full bg-white/40 backdrop-blur-sm dark:bg-black/35" />

        <div className="pointer-events-none absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
          <span className="block h-[5px] w-4 rounded-full bg-white/80 dark:bg-white/45" />
          <span className="block h-[5px] w-[5px] rounded-full bg-white/55 dark:bg-white/35" />
          <span className="block h-[5px] w-[5px] rounded-full bg-white/55 dark:bg-white/35" />
        </div>
      </div>

      {/* Content area — same vertical rhythm as MobilePropertyCard */}
      <div className="space-y-1.5 px-0.5">
        <div className="h-3 w-24 rounded-full skeleton-shimmer" />
        <div className="h-4 w-4/5 rounded-md skeleton-shimmer" />

        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 pt-0.5">
          <div className="h-3 w-12 rounded-full skeleton-shimmer" />
          <div className="h-3 w-14 rounded-full skeleton-shimmer" />
          <div className="h-3 w-10 rounded-full skeleton-shimmer" />
          <div className="h-3 w-14 rounded-full skeleton-shimmer" />
        </div>

        <div className="flex items-baseline gap-1.5 pt-1">
          <div className="h-5 w-24 rounded-md skeleton-shimmer" />
          <div className="h-3 w-8 rounded-full skeleton-shimmer" />
        </div>

        <div className="h-3 w-28 rounded-full skeleton-shimmer" />
      </div>
    </div>
  );
}

// Compact, non-interactive overlay shown over the map while pins are being
// refreshed but the surface already has content. Designed to be paired with
// `surfaceTransitionLoading` from PropertyMapView.
export function PropertiesMapPinsLoadingBadge({
  className,
  label = 'Actualizando arrendamientos en el mapa',
}: {
  className?: string;
  label?: string;
}) {
  return (
    <div
      className={cn(
        'pointer-events-none inline-flex h-10 min-w-16 items-center justify-center rounded-full border border-white/70 bg-white/95 px-5 shadow-[0_10px_30px_rgba(15,23,42,0.16)] backdrop-blur-xl dark:border-zinc-800/80 dark:bg-black/85',
        className,
      )}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <span className="flex items-center gap-1.5" aria-hidden="true">
        {[0, 1, 2].map((index) => (
          <span
            key={index}
            className="h-1.5 w-1.5 animate-[rc-map-dot_900ms_ease-in-out_infinite] rounded-full bg-slate-900 dark:bg-white"
            style={{ animationDelay: `${index * 140}ms` }}
          />
        ))}
      </span>
    </div>
  );
}

function PropertiesPaginationSkeleton({ mobile = false }: { mobile?: boolean }) {
  return (
    <div className={cn('flex items-center justify-center gap-2', mobile ? 'mt-0' : 'mt-12 mb-8')}>
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <div
          key={index}
          className={cn(
            'rounded-full skeleton-shimmer',
            mobile ? 'h-8 w-8' : index === 4 ? 'h-10 w-10' : 'h-10 w-10',
          )}
        />
      ))}
    </div>
  );
}

function buildDesktopEntries(cardCount: number, includePromoCard = true) {
  if (!includePromoCard) {
    return Array.from({ length: cardCount }, (_, index) => ({
      kind: 'property' as const,
      key: `property-${index}`,
    }));
  }

  const entries: Array<{ kind: 'property' | 'promo'; key: string }> = [];
  const promoSlot = Math.min(2, Math.max(cardCount - 1, 0));

  for (let index = 0; index < cardCount; index += 1) {
    if (index === promoSlot) {
      entries.push({ kind: 'promo', key: `promo-${index}` });
    }

    entries.push({ kind: 'property', key: `property-${index}` });
  }

  return entries;
}

export function PropertiesCardsSurfaceSkeleton({
  viewport,
  cardCount = DEFAULT_PROPERTIES_SKELETON_CARD_COUNT,
  bare = false,
  includeHeader = false,
  includePagination = true,
  includePromoCard = true,
  className,
}: PropertiesCardsSurfaceSkeletonProps) {
  if (viewport === 'mobile') {
    const visibleRowCount = Math.min(cardCount, DEFAULT_PROPERTIES_MOBILE_SHEET_ROW_COUNT);

    return (
      <div
        className={cn(
          'flex h-full flex-col bg-white dark:bg-black',
          !bare && 'overflow-hidden rounded-[2rem] shadow-sm',
          className,
        )}
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-2">
          <div className="flex flex-col gap-5">
            {Array.from({ length: visibleRowCount }).map((_, index) => (
              <PropertiesMobileVerticalCardSkeleton key={index} />
            ))}
          </div>
        </div>

        {includePagination ? (
          <div className="border-t border-gray-100 px-4 py-4 dark:border-zinc-900">
            <PropertiesPaginationSkeleton mobile />
          </div>
        ) : null}
      </div>
    );
  }

  const entries = buildDesktopEntries(cardCount, includePromoCard);

  return (
    <div className={cn('flex h-full flex-col', bare ? 'bg-transparent' : 'rounded-3xl bg-slate-50/60 shadow-sm dark:bg-zinc-950/70', className)}>
      <div className={cn('min-h-0 flex-1 overflow-y-auto scroll-smooth', bare ? 'bg-transparent px-0 py-4' : 'rounded-b-3xl bg-white/80 px-4 py-5 dark:bg-black/80')}>
        {includeHeader ? (
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="space-y-2">
              <div className="h-4 w-32 rounded-full skeleton-shimmer" />
              <div className="h-3 w-52 rounded-full skeleton-shimmer" />
            </div>
            <div className="h-10 w-40 rounded-full skeleton-shimmer" />
          </div>
        ) : null}

        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:gap-5">
          {entries.map((entry) =>
            entry.kind === 'promo' ? (
              <PropertiesPromoCardSkeleton key={entry.key} />
            ) : (
              <PropertiesDesktopCardSkeleton key={entry.key} />
            ),
          )}
        </div>

        {includePagination ? <PropertiesPaginationSkeleton /> : null}
      </div>
    </div>
  );
}

export function PropertiesMapSurfaceSkeleton({
  viewport,
  className,
  showListPill = false,
}: PropertiesMapSurfaceSkeletonProps) {
  const isMobile = viewport === 'mobile';

  return (
    <div
      className={cn(
        'relative h-full w-full overflow-hidden bg-[#f0f3f4] dark:bg-zinc-950',
        !isMobile && 'md:rounded-[1.75rem] md:border md:border-gray-200 dark:md:border-zinc-800',
        className,
      )}
    >
      <div
        className="absolute inset-0 opacity-25 dark:opacity-20"
        style={{ backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)', backgroundSize: '30px 30px', color: 'rgb(148 163 184)' }}
      />

      <div className="absolute left-4 right-4 top-4 flex justify-center">
        <div className="h-10 w-[10.5rem] rounded-full skeleton-shimmer shadow-sm" />
      </div>

      {[
        { top: '30%', left: '40%' },
        { top: '45%', left: '25%' },
        { top: '55%', left: '60%' },
        { top: '20%', left: '70%' },
        { top: '70%', left: '35%' },
        { top: '65%', left: '75%' },
      ].map((position, index) => (
        <div key={index} className="absolute flex flex-col items-center" style={position}>
          <div className="h-3 w-3 rounded-full bg-slate-300/80 shadow-[0_0_0_6px_rgba(203,213,225,0.22)] dark:bg-emerald-400/45 dark:shadow-[0_0_0_6px_rgba(16,185,129,0.10)]" />
        </div>
      ))}

      {showListPill ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center">
          <div className="h-11 w-[8.25rem] rounded-full skeleton-shimmer" />
        </div>
      ) : null}
    </div>
  );
}

export function SelectedPropertyCardSkeleton({ mobile = false, className = '' }: { mobile?: boolean; className?: string }) {
  if (mobile) {
    return (
      <div className={cn("pointer-events-auto flex flex-row h-[160px] rounded-[1.5rem] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.2)] border border-gray-100 relative dark:border-zinc-800 dark:bg-zinc-950 dark:shadow-[0_18px_54px_rgba(0,0,0,0.75)]", className || "w-full")}>
        <div className="relative flex-shrink-0 w-[38%] h-full rounded-l-[1.5rem] skeleton-shimmer" />
        <div className="flex flex-1 flex-col justify-between p-3 py-4 rounded-r-[1.5rem]">
          <div className="mt-1 h-3 w-24 rounded-full skeleton-shimmer" />
          <div className="mt-2 h-4 w-5/6 rounded-full skeleton-shimmer" />
          <div className="mt-3 h-3 w-3/4 rounded-full skeleton-shimmer" />
          <div className="mt-auto flex items-baseline gap-2">
            <div className="h-5 w-20 rounded-full skeleton-shimmer" />
            <div className="h-3 w-8 rounded-full skeleton-shimmer" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("pointer-events-auto flex flex-col w-[280px] sm:w-[320px] rounded-[1.5rem] shadow-2xl shadow-gray-900/15 border border-gray-100 bg-white relative dark:border-zinc-800 dark:bg-zinc-950 dark:shadow-[0_24px_70px_rgba(0,0,0,0.75)]", className)}>
      <div className="relative w-full aspect-[4/3] rounded-t-[1.5rem] skeleton-shimmer" />
      <div className="flex flex-col p-4 rounded-b-[1.5rem]">
        <div className="h-3 w-32 rounded-full skeleton-shimmer" />
        <div className="mt-2 h-4 w-5/6 rounded-full skeleton-shimmer" />
        <div className="mt-3 h-3 w-3/4 rounded-full skeleton-shimmer" />
        <div className="mt-4 flex items-baseline gap-2">
          <div className="h-6 w-24 rounded-full skeleton-shimmer" />
          <div className="h-3 w-10 rounded-full skeleton-shimmer" />
        </div>
      </div>
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-white drop-shadow-sm pointer-events-none dark:border-t-zinc-950" />
    </div>
  );
}

export function PropertiesMobileListSheetSkeleton({
  cardCount = DEFAULT_PROPERTIES_MOBILE_SHEET_ROW_COUNT,
  className,
}: {
  cardCount?: number;
  className?: string;
}) {
  return (
    <div className={cn('h-full overflow-hidden rounded-t-[2rem] bg-white shadow-[0_-12px_40px_rgba(15,23,42,0.16)] dark:bg-black dark:shadow-[0_-18px_50px_rgba(0,0,0,0.65)]', className)}>
      <div className="relative border-b border-gray-100 px-5 pb-3 pt-2.5 dark:border-zinc-900">
        <div className="absolute left-1/2 top-1.5 h-1 w-12 -translate-x-1/2 rounded-full bg-gray-300 dark:bg-zinc-700" aria-hidden="true" />

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="h-5 w-44 rounded-full skeleton-shimmer" />
          <div className="h-8 w-8 rounded-full skeleton-shimmer" />
        </div>
      </div>

      <div className="h-[calc(100%-64px)] overflow-hidden">
        <PropertiesCardsSurfaceSkeleton viewport="mobile" bare cardCount={cardCount} includePagination={false} />
      </div>
    </div>
  );
}

export function PropertiesMobileSheetPeekSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('absolute inset-x-0 bottom-0 z-20', className)}>
      <div className="rounded-t-[2rem] bg-white px-5 pb-[calc(env(safe-area-inset-bottom)+0.9rem)] pt-2 shadow-[0_-15px_40px_rgba(0,0,0,0.15)] dark:bg-black dark:shadow-[0_-18px_50px_rgba(0,0,0,0.65)]">
        <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-300 dark:bg-zinc-700" aria-hidden="true" />

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-4 w-40 rounded-full skeleton-shimmer" />
            <div className="h-3 w-24 rounded-full skeleton-shimmer" />
          </div>

          <div className="h-8 w-16 rounded-full skeleton-shimmer" />
        </div>
      </div>
    </div>
  );
}

export function PublicPropertiesPageSkeleton({
  viewport,
  cardCount = DEFAULT_PROPERTIES_SKELETON_CARD_COUNT,
}: PropertiesPageSkeletonProps) {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-900 dark:bg-black dark:text-zinc-100">
      {viewport === 'desktop' ? <PropertiesDesktopHeaderSkeleton /> : <PropertiesMobileHeaderSkeleton />}

      {viewport === 'desktop' ? (
        <div className="flex h-[calc(100vh-76px)]">
          <div className="w-full overflow-hidden bg-[#f9fafb] p-6 dark:bg-black lg:w-[55%] xl:w-[60%]">
            <PropertiesCardsSurfaceSkeleton
              viewport="desktop"
              bare
              includeHeader
              cardCount={cardCount}
              className="h-full"
            />
          </div>

          <div className="hidden h-full border-l border-gray-200 dark:border-zinc-900 lg:block lg:w-[45%] xl:w-[40%]">
            <PropertiesMapSurfaceSkeleton viewport="desktop" className="h-full" />
          </div>
        </div>
      ) : viewport === 'mobile-list' ? (
        <div className="relative bg-[#f0f3f4] dark:bg-black" style={{ height: 'calc(100dvh - 76px)' }}>
          <PropertiesMapSurfaceSkeleton viewport="mobile" className="absolute inset-0" mobileBottomInsetPx={112} />
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" />

          <div className="absolute inset-x-0 bottom-0" style={{ height: 'min(85dvh, calc(100dvh - 76px))' }}>
            <PropertiesMobileListSheetSkeleton cardCount={cardCount} />
          </div>
        </div>
      ) : (
        <div className="relative" style={{ height: 'calc(100dvh - 76px)' }}>
          <PropertiesMapSurfaceSkeleton viewport="mobile" className="h-full" mobileBottomInsetPx={112} />
          <PropertiesMobileSheetPeekSkeleton />
        </div>
      )}
    </div>
  );
}

export function PublicFavoritesPageSkeleton({
  mobile = false,
  cardCount,
  className,
}: {
  mobile?: boolean;
  cardCount?: number;
  className?: string;
}) {
  const resolvedCardCount = mobile
    ? Math.min(cardCount ?? DEFAULT_PROPERTIES_MOBILE_SHEET_ROW_COUNT, DEFAULT_PROPERTIES_MOBILE_SHEET_ROW_COUNT)
    : cardCount ?? 6;

  return (
    <div className={cn('w-full', className)}>
      <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="space-y-3">
          <div className="h-3 w-28 rounded-full skeleton-shimmer" />
          <div className="h-8 w-48 rounded-full skeleton-shimmer md:h-10 md:w-64" />
          <div className="h-4 w-full max-w-xl rounded-full skeleton-shimmer" />
          <div className="h-4 w-4/5 max-w-lg rounded-full skeleton-shimmer" />
        </div>

        <div className="h-10 w-40 rounded-full skeleton-shimmer" />
      </div>

      {mobile ? (
        <div className="overflow-hidden rounded-[2rem] border border-border bg-card shadow-sm">
          <PropertiesCardsSurfaceSkeleton
            viewport="mobile"
            bare
            cardCount={resolvedCardCount}
            includePagination={false}
          />
        </div>
      ) : (
        <PropertiesCardsSurfaceSkeleton
          viewport="desktop"
          bare
          includeHeader={false}
          includePagination={false}
          includePromoCard={false}
          cardCount={resolvedCardCount}
        />
      )}
    </div>
  );
}
